import { PushNotificationPrompt } from '@/components/push/push-notification-prompt'
import { InstallPrompt } from '@/components/pwa/install-prompt'
import { RouteAccessGuard } from '@/components/route-access-guard/route-access-guard'
import { useTheme } from '@/lib/hooks/use-theme'
import { logout } from '@/store/slices/authSlice'
import { useAppDispatch, useAuth } from '@/store/hooks'
import React, { useEffect, useState } from 'react'
import ProntuarioReturnButton from '@/components/prontuario/prontuario-return-button'
import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { AdminNavbar } from './admin-navbar'
import { AdminSidebar } from './admin-sidebar'
import { ChatDock } from '@/components/atendimento/chat-dock/chat-dock'
import OrphanPixMatchDialogHost from '@/components/billing/orphan-pix-match-dialog'
import { ChatDockProvider, useChatDock } from '@/lib/providers/chat-dock-provider'
import { AiUserPanel } from '@/components/ai-user/ai-user-panel'
import { AiUserRunnerProvider } from '@/lib/ai-user/ai-user-runner'
import { isAiUserType } from '@/lib/auth/restricted-viewer'

export const AdminLayout = () => (
    <ChatDockProvider>
        <AdminLayoutContent />
    </ChatDockProvider>
)

const AdminLayoutContent = () => {
    const [sidebarOpen, setSidebarOpen] = useState(false)
    const theme = useTheme()
    const location = useLocation()
    const dispatch = useAppDispatch()

    const { user, isAuthenticated, token } = useAuth()
    const { isDockOpen } = useChatDock()

    // Clientes (userType CLIENT) não acessam o painel — apenas o aplicativo.
    // Se um token de cliente cair aqui (ex: injetado), a sessão é encerrada.
    const isClient = user?.userType === 'CLIENT'
    // Usuário de IA trabalha com o painel do Modo IA ao lado da aplicação (docs/ai-user.md).
    const isAiUser = isAiUserType(user?.userType)
    useEffect(() => {
        if (isClient) {
            dispatch(logout())
        }
    }, [isClient, dispatch])

    if (!isAuthenticated || !user || !token || isClient) {
        return <Navigate to="/login" replace />
    }

    // Páginas full-bleed: sem o wrapper p-6/max-w (a própria página controla
    // altura e scroll). Atendimentos é layout de chat e preenche o main.
    const isFullWidthPage =
        location.pathname.startsWith('/atendimento/chats') ||
        location.pathname.startsWith('/sales/automations/flows/')

    // Sidebar colapsada só onde realmente queremos (não em atendimentos).
    const isSidebarCollapsedByRoute =
        location.pathname.startsWith('/sales/automations/flows/') ||
        location.pathname.startsWith('/support')

    // Com o dock aberto o menu fica em ícones e NÃO volta a expandir: a lista de
    // conversas ocupa a largura dele, e os dois abertos ao mesmo tempo não cabem.
    const isSidebarCollapsed = isSidebarCollapsedByRoute || isDockOpen

    return (
        <React.Fragment>
            <div data-theme={theme} className="flex h-screen overflow-hidden">
                <div className="hidden lg:block">
                    <AdminSidebar collapsed={isSidebarCollapsed} />
                </div>
                <ChatDock />
                {/* Aviso da baixa manual quando já entrou um Pix sem vínculo de mesmo valor. */}
                <OrphanPixMatchDialogHost />
                {sidebarOpen && (
                    <div className="lg:hidden fixed inset-0 z-50">
                        <div
                            className="fixed inset-0 bg-black/50"
                            onClick={() => setSidebarOpen(false)}
                        ></div>
                        <div className="fixed left-0 top-0 bottom-0 z-50 shadow-2xl">
                            <AdminSidebar
                                onItemClick={() => setSidebarOpen(false)}
                            />
                        </div>
                    </div>
                )}
                <div className="flex flex-col flex-1 overflow-hidden">
                    <AdminNavbar
                        onMenuClick={() => setSidebarOpen(!sidebarOpen)}
                    />
                    <main
                        className={`flex-1 overflow-y-auto bg-[#F9F9F9]! ${isFullWidthPage ? '' : ''}`}
                    >
                        {isFullWidthPage ? (
                            <RouteAccessGuard>
                                <Outlet />
                            </RouteAccessGuard>
                        ) : (
                            <div className="p-6 !max-w-[1300px] mx-auto">
                                <RouteAccessGuard>
                                    <Outlet />
                                </RouteAccessGuard>
                            </div>
                        )}
                    </main>
                </div>
                {isAiUser && (
                    <AiUserRunnerProvider>
                        <AiUserPanel />
                    </AiUserRunnerProvider>
                )}
            </div>
            {/* Volta ao prontuário: mora aqui porque o fluxo aberto a partir dele
                atravessa telas que não se conhecem (agendamento, cobrança, detalhe,
                guia). Ele mesmo decide quando aparecer. */}
            <ProntuarioReturnButton />
            <InstallPrompt />
            <PushNotificationPrompt />
        </React.Fragment>
    )
}
